import React, { useCallback, useEffect, useMemo, useState } from "react"
import {
  clearStudentSession,
  getStoredStudentInfo,
  isStudentLoggedIn,
  saveStudentSession,
  studentLogin,
  validateLearnerSession,
} from "../../api/studentAuth"
import {
  LEARNER_LOGIN_PATH,
  LEARNER_SESSION_CLEARED_EVENT,
} from "./constants"
import { getLearnerApiErrorMessage } from "./errors"
import { LearnerAuthContext } from "./LearnerAuthContext"
import type { LearnerAuthStatus, StudentInfo, StudentLoginPayload } from "./types"

interface LearnerAuthProviderProps {
  children: React.ReactNode
}

const LearnerAuthProvider = ({ children }: LearnerAuthProviderProps) => {
  const [status, setStatus] = useState<LearnerAuthStatus>("booting")
  const [student, setStudent] = useState<StudentInfo | null>(getStoredStudentInfo())

  const logout = useCallback((options?: { redirect?: boolean }) => {
    clearStudentSession()
    if (options?.redirect !== false) {
      // Full navigation first — avoid a React re-render on a /learner/*
      // URL that would fall through to the teacher app (/login).
      window.location.replace(LEARNER_LOGIN_PATH)
      return
    }
    setStudent(null)
    setStatus("unauthenticated")
  }, [])

  const refreshProfile = useCallback(async () => {
    const profile = await validateLearnerSession()
    setStudent(profile)
    setStatus("authenticated")
    return profile
  }, [])

  const login = useCallback(async (payload: StudentLoginPayload) => {
    const res = await studentLogin(payload)
    const data = res.data?.data

    if (!res.data?.success || !data?.access_token || !data?.refresh_token || !data?.student) {
      throw new Error(res.data?.message || "Login failed. Please try again.")
    }

    saveStudentSession(data.access_token, data.refresh_token, data.student)
    setStudent(data.student)
    setStatus("authenticated")

    try {
      const profile = await validateLearnerSession()
      setStudent(profile)
      return profile
    } catch {
      return data.student
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    const boot = async () => {
      if (!isStudentLoggedIn()) {
        if (!cancelled) {
          setStudent(null)
          setStatus("unauthenticated")
        }
        return
      }

      try {
        const profile = await validateLearnerSession()
        if (!cancelled) {
          setStudent(profile)
          setStatus("authenticated")
        }
      } catch (error) {
        clearStudentSession()
        if (!cancelled) {
          setStudent(null)
          setStatus("unauthenticated")
          if (
            window.location.pathname.startsWith("/learner") &&
            !window.location.pathname.startsWith(LEARNER_LOGIN_PATH)
          ) {
            window.location.assign(LEARNER_LOGIN_PATH)
          }
        }
        console.warn("Learner session invalid:", getLearnerApiErrorMessage(error))
      }
    }

    boot()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    const onCleared = () => {
      setStudent(null)
      setStatus("unauthenticated")
    }
    window.addEventListener(LEARNER_SESSION_CLEARED_EVENT, onCleared)
    return () => window.removeEventListener(LEARNER_SESSION_CLEARED_EVENT, onCleared)
  }, [])

  const value = useMemo(
    () => ({
      status,
      student,
      isAuthenticated: status === "authenticated",
      isBooting: status === "booting",
      login,
      logout,
      refreshProfile,
    }),
    [status, student, login, logout, refreshProfile]
  )

  return (
    <LearnerAuthContext.Provider value={value}>
      {children}
    </LearnerAuthContext.Provider>
  )
}

export default LearnerAuthProvider
